import React, { useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { usePermissions } from '../../../../contexts/PermissionsContext';
import { useAuth } from '../../../../contexts/authContext';
import ReportsPanelBulkActions from './ReportsPanelBulkActions';
import supabase from '../../../../supabaseClient';
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, LineChart, Line } from 'recharts';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import './ReportsPanel.css';

// Role-based feature configuration
const ROLE_FEATURES = {
  student: {
    showAnalytics: false,
    showExport: false,
    showBulkActions: false,
    showGroupSelector: false,
    showStudentPerformance: false,
    showAssignmentBreakdowns: false,
    showCreateReport: false,
    maxReportsPerPage: 10
  },
  teacher: {
    showAnalytics: true,
    showExport: true,
    showBulkActions: true,
    showGroupSelector: true,
    showStudentPerformance: true,
    showAssignmentBreakdowns: true,
    showCreateReport: true,
    maxReportsPerPage: 25
  },
  manager: {
    showAnalytics: true,
    showExport: true,
    showBulkActions: true,
    showGroupSelector: true,
    showStudentPerformance: true,
    showAssignmentBreakdowns: true,
    showCreateReport: true,
    maxReportsPerPage: 50
  },
  admin: {
    showAnalytics: true,
    showExport: true,
    showBulkActions: true,
    showGroupSelector: false,
    showStudentPerformance: false,
    showAssignmentBreakdowns: false,
    showCreateReport: true,
    maxReportsPerPage: 100
  },
  superadmin: {
    showAnalytics: true,
    showExport: true,
    showBulkActions: true,
    showGroupSelector: false,
    showStudentPerformance: false,
    showAssignmentBreakdowns: false,
    showCreateReport: true,
    maxReportsPerPage: 100
  }
};

const DEMO_GROUPS = [
  { id: 'demo1', name: 'Demo Group A' },
  { id: 'demo2', name: 'Demo Group B' }
];
const DEMO_ANALYTICS = {
  completionRate: 0.85,
  averageGrade: 92,
  overdueAssignments: 2,
  activeStudents: 18,
  gradeDistribution: [
    { name: 'A', value: 10 },
    { name: 'B', value: 5 },
    { name: 'C', value: 3 },
    { name: 'D', value: 0 },
    { name: 'F', value: 0 }
  ],
  trend: [
    { date: '2025-07-01', completed: 5 },
    { date: '2025-07-08', completed: 7 },
    { date: '2025-07-15', completed: 10 }
  ]
};

// Toast notification system
const Toast = ({ message, type = 'info', onClose }) => (
  <div 
    className={`toast-notification toast-${type}`}
    onClick={onClose}
    role="alert"
    aria-live="polite"
  >
    {message}
  </div>
);

// Modal component for drill-downs
const Modal = ({ isOpen, onClose, title, children }) => {
  if (!isOpen) return null;
  
  return (
    <div 
      className="modal-overlay"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
    >
      <div 
        className="modal-content"
        onClick={e => e.stopPropagation()}
      >
        <div className="modal-header">
          <h3 id="modal-title" className="modal-title">{title}</h3>
          <button 
            onClick={onClose}
            className="modal-close-btn"
            aria-label="Close modal"
          >
            ×
          </button>
        </div>
        {children}
      </div>
    </div>
  );
};

// Skeleton loader component
const Skeleton = ({ width = '100%', height = 32 }) => (
  <div 
    className="skeleton-loader"
    style={{ height, width }}
    aria-label="Loading content"
  />
);

const ReportsPanel = ({ role, locale = 'en' }) => {
  // Get role-based features
  const features = ROLE_FEATURES[role] || ROLE_FEATURES.student;
  
  // Contexts
  const { user } = useAuth();
  const { roles } = usePermissions();

  // State
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedReports, setSelectedReports] = useState([]);
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [students, setStudents] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [demoMode, setDemoMode] = useState(false);
  const [toast, setToast] = useState(null);
  const [skeleton, setSkeleton] = useState(false);
  // Manager-specific: managed teachers and filter
  const [managedTeachers, setManagedTeachers] = useState([]); // [{id, display_name}]
  const [selectedTeacherId, setSelectedTeacherId] = useState('');
  
  // Advanced filtering and pagination
  const [filters, setFilters] = useState({ dateFrom: '', dateTo: '', status: '', type: '' });
  const [currentPage, setCurrentPage] = useState(1);
  const [modalState, setModalState] = useState({ isOpen: false, type: null, data: null });

  // Load user preferences
  useEffect(() => {
    const savedPrefs = localStorage.getItem(`reportsPanel_${user?.id}_${role}`);
    if (savedPrefs) {
      const prefs = JSON.parse(savedPrefs);
      setFilters(prefs.filters || filters);
      setSelectedGroup(prefs.selectedGroup || null);
    }
  }, [user?.id, role]);

  // Save preferences
  const savePreferences = useCallback(() => {
    if (user?.id) {
      localStorage.setItem(`reportsPanel_${user.id}_${role}`, JSON.stringify({ filters, selectedGroup }));
    }
  }, [user?.id, role, filters, selectedGroup]);

  useEffect(() => { savePreferences(); }, [savePreferences]);

  // Load teachers managed by the current user (manager role)
  useEffect(() => {
    const loadManagedTeachers = async () => {
      if (role !== 'manager' || !user?.id) return;
      try {
        const { data: tmRows, error: tmErr } = await supabase
          .from('academy_teacher_manager')
          .select('teacher_id')
          .eq('manager_id', user.id);
        if (tmErr) return;
        const teacherIds = (tmRows || []).map(r => r.teacher_id).filter(Boolean);
        if (!teacherIds.length) { setManagedTeachers([]); return; }
        const { data: profs } = await supabase
          .from('profiles')
          .select('id, display_name')
          .in('id', teacherIds);
        setManagedTeachers((profs || []).map(p => ({ id: p.id, name: p.display_name || p.id })));
      } catch { /* ignore */ }
    };
    loadManagedTeachers();
  }, [role, user?.id]);

  // Fetch groups for teachers/managers
  useEffect(() => {
    if (role === 'teacher' || role === 'manager') {
      const fetchGroups = async () => {
        setSkeleton(true);
        try {
          if (role === 'teacher') {
            // Teacher: direct filter by teacher_id on groups
            const { data, error } = await supabase
              .from('academy_groups')
              .select('*')
              .eq('teacher_id', user.id);
            if (error || !data || data.length === 0) {
              setGroups(DEMO_GROUPS);
              setDemoMode(true);
            } else {
              setGroups(data);
              setDemoMode(false);
            }
          } else {
            // Manager: direct manager filter with optional teacher filter; fallback via courses
            let groupsFetched = [];
            try {
              let q = supabase
                .from('academy_groups')
                .select('*')
                .eq('manager_id', user.id);
              if (selectedTeacherId) q = q.eq('teacher_id', selectedTeacherId);
              const { data, error } = await q;
              if (!error) groupsFetched = data || [];
            } catch (_) { /* ignore */ }

            if (groupsFetched.length > 0) {
              setGroups(groupsFetched);
              setDemoMode(false);
            } else {
              const { data: courses, error: courseErr } = await supabase
                .from('academy_courses')
                .select('id')
                .eq('manager_id', user.id);
              if (courseErr) throw courseErr;
              const ids = (courses || []).map(c => c.id);
              if (!ids.length) {
                setGroups(DEMO_GROUPS);
                setDemoMode(true);
              } else {
                let q = supabase
                  .from('academy_groups')
                  .select('*')
                  .in('course_id', ids);
                if (selectedTeacherId) q = q.eq('teacher_id', selectedTeacherId);
                const { data: groupsData, error: groupsErr } = await q;
                if (groupsErr || !groupsData || groupsData.length === 0) {
                  setGroups(DEMO_GROUPS);
                  setDemoMode(true);
                } else {
                  setGroups(groupsData);
                  setDemoMode(false);
                }
              }
            }
          }
        } catch (e) {
          setGroups(DEMO_GROUPS);
          setDemoMode(true);
        }
        setSkeleton(false);
      };
      if (user && role) fetchGroups();
    }
  }, [user, role, selectedTeacherId]);

  // Fetch reports for the selected group (or all if not teacher/manager)
  useEffect(() => {
    const fetchReports = async () => {
      setLoading(true);
      let query = supabase.from('academy_reports').select('*');
      if (role === 'teacher' || role === 'manager') {
        if (selectedGroup && !demoMode) {
          query = query.eq('group_id', selectedGroup.id);
        } else if (demoMode) {
          setReports([]);
          setLoading(false);
          return;
        }
      } else if (role === 'student') {
        query = query.eq('student_id', user.id);
      }
      const { data, error } = await query;
      if (error) setReports([]);
      else setReports(data || []);
      setLoading(false);
    };
    if ((role !== 'teacher' && role !== 'manager') || selectedGroup || demoMode) fetchReports();
  }, [user, role, selectedGroup, demoMode]);

  // Fetch students and assignments for the selected group (for analytics)
  useEffect(() => {
    if ((role === 'teacher' || role === 'manager') && selectedGroup && !demoMode) {
      const fetchStudentsAndAssignments = async () => {
        setSkeleton(true);
        const [{ data: studentsData }, { data: assignmentsData }] = await Promise.all([
          supabase.from('academy_students').select('*').eq('group_id', selectedGroup.id),
          supabase.from('academy_assignments').select('*').eq('group_id', selectedGroup.id)
        ]);
        setStudents(studentsData || []);
        setAssignments(assignmentsData || []);
        setSkeleton(false);
      };
      fetchStudentsAndAssignments();
    } else if (demoMode && selectedGroup) {
      setStudents([
        { id: 1, name: 'Alice', grade: 95, completed: true },
        { id: 2, name: 'Bob', grade: 88, completed: false },
        { id: 3, name: 'Charlie', grade: 90, completed: true }
      ]);
      setAssignments([
        { id: 1, title: 'Demo Assignment 1', due: '2025-07-10', completed: 2, total: 3 },
        { id: 2, title: 'Demo Assignment 2', due: '2025-07-17', completed: 1, total: 3 }
      ]);
    }
  }, [selectedGroup, role, demoMode]);

  // Compute analytics for the selected group
  useEffect(() => {
    if ((role === 'teacher' || role === 'manager') && selectedGroup) {
      if (demoMode) {
        setAnalytics(DEMO_ANALYTICS);
        return;
      }
      // Real analytics computation
      if (students.length && assignments.length) {
        const completed = students.filter(s => s.completed).length;
        const completionRate = completed / students.length;
        const averageGrade = students.reduce((sum, s) => sum + (s.grade || 0), 0) / students.length;
        const overdueAssignments = assignments.filter(a => new Date(a.due) < new Date() && a.completed < a.total).length;
        const activeStudents = students.length;
        // Grade distribution for pie
        const gradeDistribution = [
          { name: 'A', value: students.filter(s => s.grade >= 90).length },
          { name: 'B', value: students.filter(s => s.grade >= 80 && s.grade < 90).length },
          { name: 'C', value: students.filter(s => s.grade >= 70 && s.grade < 80).length },
          { name: 'D', value: students.filter(s => s.grade >= 60 && s.grade < 70).length },
          { name: 'F', value: students.filter(s => s.grade < 60).length }
        ];
        // Trend (fake data for now)
        const trend = [
          { date: '2025-07-01', completed: 5 },
          { date: '2025-07-08', completed: 7 },
          { date: '2025-07-15', completed: 10 }
        ];
        setAnalytics({ completionRate, averageGrade, overdueAssignments, activeStudents, gradeDistribution, trend });
      }
    } else {
      setAnalytics(null);
    }
  }, [students, assignments, selectedGroup, role, demoMode]);



  // Bulk select logic
  const toggleSelect = id => {
    setSelectedReports(prev =>
      prev.includes(id) ? prev.filter(rid => rid !== id) : [...prev, id]
    );
  };

  // Export analytics as CSV
  const exportCSV = useCallback(() => {
    if (!analytics) return;
    const csv = [
      'Metric,Value',
      `Completion Rate,${(analytics.completionRate * 100).toFixed(1)}%`,
      `Average Grade,${analytics.averageGrade.toFixed(1)}`,
      `Overdue Assignments,${analytics.overdueAssignments}`,
      `Active Students,${analytics.activeStudents}`
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    saveAs(blob, `${selectedGroup ? selectedGroup.name : 'group'}_analytics.csv`);
    showToast('Analytics exported as CSV!', 'success');
  }, [analytics, selectedGroup]);

  // Export analytics as PDF
  const exportPDF = useCallback(() => {
    if (!analytics) return;
    try {
      const doc = new jsPDF();
      doc.setFontSize(20);
      doc.text(`${selectedGroup ? selectedGroup.name : 'Group'} Analytics`, 20, 20);
      
      const data = [
        ['Metric', 'Value'],
        ['Completion Rate', `${(analytics.completionRate * 100).toFixed(1)}%`],
        ['Average Grade', analytics.averageGrade.toFixed(1)],
        ['Overdue Assignments', analytics.overdueAssignments.toString()],
        ['Active Students', analytics.activeStudents.toString()]
      ];
      
      doc.autoTable({ head: [data[0]], body: data.slice(1), startY: 30 });
      doc.save(`${selectedGroup ? selectedGroup.name : 'group'}_analytics.pdf`);
      showToast('Analytics exported as PDF!', 'success');
    } catch (error) {
      showToast('Error exporting PDF', 'error');
    }
  }, [analytics, selectedGroup]);

  // UI helpers
  const showToast = useCallback((msg, type = 'info') => {
    setToast({ message: msg, type });
    setTimeout(() => setToast(null), 3000);
  }, []);

  const openModal = useCallback((type, data) => {
    setModalState({ isOpen: true, type, data });
  }, []);

  const closeModal = useCallback(() => {
    setModalState({ isOpen: false, type: null, data: null });
  }, []);

  // Advanced filtering with pagination
  const filteredReports = useMemo(() => {
    let filtered = reports.filter(report => {
      const matchesSearch = report.title?.toLowerCase().includes(search.toLowerCase()) ||
                           report.description?.toLowerCase().includes(search.toLowerCase());
      const matchesDateFrom = !filters.dateFrom || new Date(report.created_at) >= new Date(filters.dateFrom);
      const matchesDateTo = !filters.dateTo || new Date(report.created_at) <= new Date(filters.dateTo);
      const matchesStatus = !filters.status || report.status === filters.status;
      return matchesSearch && matchesDateFrom && matchesDateTo && matchesStatus;
    });
    
    const startIndex = (currentPage - 1) * features.maxReportsPerPage;
    return {
      reports: filtered.slice(startIndex, startIndex + features.maxReportsPerPage),
      totalReports: filtered.length,
      totalPages: Math.ceil(filtered.length / features.maxReportsPerPage)
    };
  }, [reports, search, filters, currentPage, features.maxReportsPerPage]);

  useEffect(() => { setCurrentPage(1); }, [search, filters]);

  // Assignment breakdown panel
  const AssignmentBreakdownPanel = ({ assignment }) => (
    <div style={{ padding: 12, background: '#f6f6f6', borderRadius: 8, marginTop: 8 }}>
      <div><b>Title:</b> {assignment.title}</div>
      <div><b>Due:</b> {assignment.due}</div>
      <div><b>Completed:</b> {assignment.completed} / {assignment.total}</div>
      {/* Add more breakdowns as needed */}
    </div>
  );

  // Skeleton loader
  const Skeleton = () => (
    <div style={{ background: '#eee', height: 32, width: '100%', borderRadius: 4, margin: '8px 0' }} />
  );

  // Render with role-adaptive UI
  return (
    <div className="academy-page-shell reports-panel-page">
      <div className={`reports-panel ${role}-reports`}>
        <h2>Reports Panel ({role})</h2>

        {/* Toast */}
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      {/* Advanced filters */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 16, flexWrap: 'wrap' }}>
        <input
          type="text"
          placeholder="Search reports..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ padding: 8, minWidth: 200 }}
          aria-label="Search reports"
        />
        <input
          type="date"
          placeholder="From date"
          value={filters.dateFrom}
          onChange={e => setFilters(prev => ({ ...prev, dateFrom: e.target.value }))}
          style={{ padding: 8 }}
        />
        <input
          type="date"
          placeholder="To date"
          value={filters.dateTo}
          onChange={e => setFilters(prev => ({ ...prev, dateTo: e.target.value }))}
          style={{ padding: 8 }}
        />
        <select
          value={filters.status}
          onChange={e => setFilters(prev => ({ ...prev, status: e.target.value }))}
          style={{ padding: 8 }}
        >
          <option value="">All Status</option>
          <option value="active">Active</option>
          <option value="completed">Completed</option>
          <option value="pending">Pending</option>
        </select>
      </div>

      {/* Manager: teacher filter */}
      {role === 'manager' && (
        <div style={{ marginBottom: 12 }}>
          <label htmlFor="teacher-select"><b>Teacher:</b></label>{' '}
          <select
            id="teacher-select"
            value={selectedTeacherId}
            onChange={e => { setSelectedTeacherId(e.target.value); setSelectedGroup(null); }}
            style={{ padding: 8, minWidth: 220 }}
          >
            <option value="">All teachers</option>
            {managedTeachers.map(t => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
        </div>
      )}

      {/* Group selector for teacher/manager */}
      {features.showGroupSelector && (
        <div style={{ marginBottom: 16 }}>
          <label htmlFor="group-select"><b>Group:</b></label>{' '}
          <select
            id="group-select"
            value={selectedGroup ? selectedGroup.id : ''}
            onChange={e => {
              const group = groups.find(g => g.id === e.target.value);
              setSelectedGroup(group);
            }}
            style={{ padding: 8, minWidth: 200 }}
          >
            <option value="">Select group...</option>
            {groups.map(group => (
              <option key={group.id} value={group.id}>{group.name}</option>
            ))}
          </select>
        </div>
      )}

      {/* Bulk actions - only for authorized roles */}
      {features.showBulkActions && <ReportsPanelBulkActions selectedReports={selectedReports} />}

      {/* Analytics - role-adaptive */}
      {features.showAnalytics && selectedGroup && analytics && (
        <div style={{ margin: '16px 0', background: '#f8f8f8', padding: 16, borderRadius: 8 }}>
          <h4>Analytics Dashboard</h4>
          <div style={{ display: 'flex', gap: 32, flexWrap: 'wrap' }}>
            <div>
              <div><b>Completion Rate:</b> {(analytics.completionRate * 100).toFixed(1)}%</div>
              <div><b>Average Grade:</b> {analytics.averageGrade.toFixed(1)}</div>
              <div><b>Overdue Assignments:</b> {analytics.overdueAssignments}</div>
              <div><b>Active Students:</b> {analytics.activeStudents}</div>
              {features.showExport && (
                <div style={{ marginTop: 8 }}>
                  <button onClick={exportCSV} style={{ marginRight: 8 }}>Export CSV</button>
                  <button onClick={exportPDF}>Export PDF</button>
                </div>
              )}
            </div>
            <div>
              <b>Grade Distribution</b>
              <ResponsiveContainer width={200} height={200}>
                <PieChart>
                  <Pie data={analytics.gradeDistribution} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={70}>
                    {analytics.gradeDistribution.map((entry, idx) => (
                      <Cell key={`cell-${idx}`} fill={["#82ca9d", "#8884d8", "#ffc658", "#ff8042", "#d0ed57"][idx % 5]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}

      {/* Student performance - teachers/managers only */}
      {features.showStudentPerformance && selectedGroup && students.length > 0 && (
        <div style={{ margin: '16px 0', background: '#fff', padding: 16, borderRadius: 8 }}>
          <h4>Student Performance</h4>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th>Name</th>
                <th>Grade</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {students.map(s => (
                <tr key={s.id}>
                  <td>{s.name}</td>
                  <td>{s.grade}</td>
                  <td>{s.completed ? 'Completed' : 'In Progress'}</td>
                  <td>
                    <button onClick={() => openModal('student', s)}>View Details</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Reports table with pagination */}
      <div style={{ marginTop: 16 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              {features.showBulkActions && <th></th>}
              <th>Title</th>
              <th>Description</th>
              <th>Status</th>
              <th>Created At</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={features.showBulkActions ? 5 : 4}><Skeleton /></td></tr>
            )}
            {!loading && filteredReports.reports.length === 0 && (
              <tr>
                <td colSpan={features.showBulkActions ? 5 : 4} style={{ textAlign: 'center', padding: 16 }}>
                  No reports found.
                </td>
              </tr>
            )}
            {filteredReports.reports.map(report => (
              <tr key={report.id}>
                {features.showBulkActions && (
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedReports.includes(report.id)}
                      onChange={() => toggleSelect(report.id)}
                      aria-label={`Select report ${report.title}`}
                    />
                  </td>
                )}
                <td>{report.title}</td>
                <td>{report.description}</td>
                <td>{report.status}</td>
                <td>{new Date(report.created_at).toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
        
        {/* Pagination */}
        {filteredReports.totalPages > 1 && (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8, margin: '16px 0' }}>
            <button 
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              style={{ padding: '8px 12px', cursor: currentPage === 1 ? 'not-allowed' : 'pointer' }}
            >
              Previous
            </button>
            <span>Page {currentPage} of {filteredReports.totalPages}</span>
            <button 
              onClick={() => setCurrentPage(p => Math.min(filteredReports.totalPages, p + 1))}
              disabled={currentPage === filteredReports.totalPages}
              style={{ padding: '8px 12px', cursor: currentPage === filteredReports.totalPages ? 'not-allowed' : 'pointer' }}
            >
              Next
            </button>
          </div>
        )}
      </div>

      {/* Modal for drill-downs */}
      <Modal 
        isOpen={modalState.isOpen} 
        onClose={closeModal} 
        title={modalState.type === 'student' ? 'Student Details' : 'Details'}
      >
        {modalState.type === 'student' && modalState.data && (
          <div>
            <p><b>Name:</b> {modalState.data.name}</p>
            <p><b>Grade:</b> {modalState.data.grade}</p>
            <p><b>Status:</b> {modalState.data.completed ? 'Completed' : 'In Progress'}</p>
          </div>
        )}
      </Modal>
    </div>
  </div>
  );
};

ReportsPanel.propTypes = {
  role: PropTypes.string.isRequired,
  locale: PropTypes.string,
};

export default ReportsPanel;
